
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { 
    HomeIcon, 
    SearchIcon, 
    PlusIcon, 
    UserIcon, 
    SparklesIcon, 
    SlidersIcon, 
    SunIcon, 
    MoonIcon,
    ChevronDownIcon,
    LogoutIcon,
    PanelLeftIcon,
    LayoutDashboardIcon
} from '../icons/Icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { openCommandPalette } from '../CommandPalette';
import { Button } from '../ui/Button';

export const Sidebar: React.FC = () => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    // LocalStorage-backed expanded state
    const [isExpanded, setIsExpanded] = useState<boolean>(() => {
        try {
            const saved = localStorage.getItem('promptera_sidebar_expanded');
            return saved !== null ? saved === 'true' : true;
        } catch {
            return true;
        }
    });

    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);

    const toggleExpanded = () => {
        setIsExpanded(prev => {
            const next = !prev;
            try {
                localStorage.setItem('promptera_sidebar_expanded', String(next));
            } catch {
                // Ignore localStorage errors
            }
            return next;
        });
    };

    // Close user popover on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setIsUserMenuOpen(false);
            }
        };
        if (isUserMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isUserMenuOpen]);

    const navItems = [
        { to: '/', label: 'Home', icon: HomeIcon },
        ...(user ? [{ to: '/dashboard', label: 'My Prompts', icon: LayoutDashboardIcon }] : []),
        { to: '/forge', label: 'TheForge Studio', icon: SlidersIcon },
        { to: '/browse', label: 'Browse Library', icon: SearchIcon },
    ];

    return (
        <aside 
            className={`hidden md:flex h-screen flex-col justify-between border-r border-border bg-card/60 backdrop-blur-md transition-all duration-300 ease-in-out shrink-0 relative z-30 select-none ${
                isExpanded ? 'w-64 p-4' : 'w-20 p-3 items-center'
            }`}
        >
            {/* Top Section: Brand & Navigation */}
            <div className="flex flex-col space-y-4 w-full">
                {/* Brand Header */}
                <div className={`flex items-center ${isExpanded ? 'justify-between' : 'flex-col gap-3'} pt-1`}>
                    <Link 
                        to="/" 
                        className="flex flex-col group focus:outline-none min-w-0"
                        title="Promptera Home"
                    >
                        {isExpanded ? (
                            <>
                                <img 
                                    src={theme === 'dark' ? "/logos/promptera_logo.png" : "/logos/promptera_logo_dark.png"} 
                                    alt="Promptera" 
                                    className="h-8 max-w-[155px] w-auto object-contain transition-transform group-hover:scale-[1.02]"
                                />
                                <span className="text-[10px] text-muted-foreground font-medium tracking-tight mt-0.5">
                                    Ideas to incredible images.
                                </span>
                            </>
                        ) : (
                            <img 
                                src="/logos/flavicon.png" 
                                alt="Promptera" 
                                className="h-10 w-10 object-contain transition-transform group-hover:scale-105 shrink-0"
                            />
                        )}
                    </Link>

                    {/* Sidebar Collapse / Expand Button */}
                    <button
                        type="button"
                        onClick={toggleExpanded}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/60 bg-muted/30 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer shrink-0"
                        title={isExpanded ? "Collapse Sidebar" : "Expand Sidebar"}
                        aria-label={isExpanded ? "Collapse Sidebar" : "Expand Sidebar"}
                    >
                        <PanelLeftIcon className={`h-4 w-4 transition-transform duration-200 ${!isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                </div>

                {/* Quick Search Trigger */}
                <div className="w-full">
                    {isExpanded ? (
                        <button
                            type="button"
                            onClick={() => openCommandPalette()}
                            className="w-full flex items-center justify-between px-3 h-10 rounded-xl border border-border/80 bg-muted/25 hover:bg-muted/60 hover:border-accent/30 text-muted-foreground hover:text-foreground transition-all duration-200 group cursor-pointer shadow-2xs"
                            title="Quick Search (⌘K)"
                        >
                            <div className="flex items-center gap-2.5 min-w-0">
                                <SearchIcon className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors shrink-0" />
                                <span className="text-xs font-medium truncate">Search prompts...</span>
                            </div>
                            <kbd className="inline-flex items-center gap-0.5 rounded-md border border-border/80 bg-card px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground font-semibold shadow-2xs">
                                ⌘K
                            </kbd>
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => openCommandPalette()}
                            className="group relative flex h-11 w-11 mx-auto items-center justify-center rounded-xl border border-border/80 bg-muted/25 hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-all shadow-2xs cursor-pointer"
                            title="Search prompts (⌘K)"
                        >
                            <SearchIcon className="h-5 w-5" />
                            <span className="absolute left-16 scale-0 rounded-md bg-foreground px-2.5 py-1 text-xs font-semibold text-background transition-all duration-200 group-hover:scale-100 z-50 whitespace-nowrap shadow-md">
                                Search (⌘K)
                            </span>
                        </button>
                    )}
                </div>

                {/* Primary CTA: New Prompt Button */}
                {user && (
                    <div className="w-full pt-1">
                        {isExpanded ? (
                            <Link to="/prompts/new" className="block w-full">
                                <button
                                    type="button"
                                    className="w-full h-10 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm shadow-md shadow-blue-500/25 hover:shadow-lg hover:shadow-blue-500/35 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    <PlusIcon className="h-4 w-4 stroke-[2.5]" />
                                    <span>New Prompt</span>
                                </button>
                            </Link>
                        ) : (
                            <Link to="/prompts/new" className="group relative block">
                                <button
                                    type="button"
                                    className="flex h-10 w-10 mx-auto items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-md shadow-blue-500/25 hover:scale-105 transition-all cursor-pointer"
                                    title="New Prompt"
                                >
                                    <PlusIcon className="h-5 w-5 stroke-[2.5]" />
                                </button>
                                <span className="absolute left-16 scale-0 rounded-md bg-foreground px-2.5 py-1 text-xs font-semibold text-background transition-all duration-200 group-hover:scale-100 z-50 whitespace-nowrap shadow-md">
                                    New Prompt
                                </span>
                            </Link>
                        )}
                    </div>
                )}

                {/* Navigation Links */}
                <nav className="flex flex-col space-y-1.5 w-full pt-2">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        if (isExpanded) {
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === '/'}
                                    className={({ isActive }) =>
                                        `flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 ${
                                            isActive
                                                ? 'bg-accent/15 text-accent border border-accent/25 shadow-2xs font-bold'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent'
                                        }`
                                    }
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <Icon className="h-5 w-5 shrink-0" />
                                        <span className="truncate">{item.label}</span>
                                    </div>
                                    {item.badge && (
                                        <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-accent/20 text-accent shrink-0">
                                            {item.badge}
                                        </span>
                                    )}
                                </NavLink>
                            );
                        }

                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === '/'}
                                className={({ isActive }) =>
                                    `group relative flex h-11 w-11 mx-auto items-center justify-center rounded-xl transition-all duration-150 ${
                                        isActive
                                            ? 'bg-accent text-accent-foreground shadow-sm shadow-accent/20'
                                            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                                    }`
                                }
                            >
                                <Icon className="h-5 w-5" />
                                <span className="absolute left-16 scale-0 rounded-md bg-foreground px-2.5 py-1 text-xs font-semibold text-background transition-all duration-200 group-hover:scale-100 z-50 whitespace-nowrap shadow-md">
                                    {item.label}
                                </span>
                            </NavLink>
                        );
                    })}
                </nav>

            </div>

            {/* Bottom Section: User Profile & Account Popover */}
            <div className="w-full pt-3 border-t border-border/80" ref={userMenuRef}>
                {/* Upgrade to Pro Card */}
                {isExpanded && (
                    <div className="mb-3 p-3 rounded-xl bg-card border border-border/80 hover:border-border transition-all cursor-pointer group shadow-2xs">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
                                    <SparklesIcon className="h-3.5 w-3.5" />
                                </div>
                                <span className="text-xs font-semibold text-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                    Promptera Pro
                                </span>
                            </div>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 border border-blue-200/60 dark:border-blue-800/50 uppercase tracking-wider">
                                Plan
                            </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">
                            Faster vision extraction, private prompts & 4K exports.
                        </p>
                    </div>
                )}

                {/* Theme Toggle Button for Collapsed Mode */}
                {!isExpanded && (
                    <button
                        type="button"
                        onClick={toggleTheme}
                        className="group relative flex h-10 w-10 mx-auto mb-2.5 items-center justify-center rounded-xl border border-border/70 bg-card/60 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
                        title={theme === 'dark' ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        aria-label="Toggle theme"
                    >
                        {theme === 'dark' ? <SunIcon className="h-4 w-4 text-amber-400" /> : <MoonIcon className="h-4 w-4 text-slate-700" />}
                        <span className="absolute left-16 scale-0 rounded-md bg-foreground px-2.5 py-1 text-xs font-semibold text-background transition-all duration-200 group-hover:scale-100 z-50 whitespace-nowrap shadow-md">
                            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                        </span>
                    </button>
                )}

                {user ? (
                    <div className="relative w-full">
                        {/* Account Trigger Button & Theme Companion */}
                        {isExpanded ? (
                            <div className="flex items-center gap-1.5 w-full">
                                <button
                                    type="button"
                                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                    className="flex-1 min-w-0 flex items-center justify-between p-2 rounded-xl border border-border/60 bg-muted/20 hover:bg-muted/60 hover:border-border transition-all group text-left cursor-pointer"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="relative shrink-0">
                                            <img
                                                src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}`}
                                                alt={user.displayName || 'User'}
                                                className="h-8 w-8 rounded-full border border-border object-cover"
                                            />
                                            <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-card" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-bold text-foreground truncate">{user.displayName || 'Creator'}</p>
                                            <p className="text-[10px] text-muted-foreground truncate">{user.email || 'Free Member'}</p>
                                        </div>
                                    </div>
                                    <ChevronDownIcon className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 shrink-0 ml-1 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                                </button>

                                <button
                                    type="button"
                                    onClick={toggleTheme}
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-muted/20 hover:bg-muted/60 hover:border-border text-muted-foreground hover:text-foreground transition-all cursor-pointer shadow-2xs"
                                    title={theme === 'dark' ? "Switch to Light Mode" : "Switch to Dark Mode"}
                                    aria-label="Toggle theme"
                                >
                                    {theme === 'dark' ? (
                                        <SunIcon className="h-4 w-4 text-amber-400" />
                                    ) : (
                                        <MoonIcon className="h-4 w-4 text-slate-700" />
                                    )}
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                className="group relative flex h-11 w-11 mx-auto items-center justify-center rounded-full p-0.5 ring-2 ring-border/80 hover:ring-accent transition-all focus:outline-none cursor-pointer"
                                title={user.displayName || "Account"}
                            >
                                <img
                                    src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}`}
                                    alt={user.displayName || 'User'}
                                    className="h-full w-full rounded-full object-cover"
                                />
                                <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-card" />
                                <span className="absolute left-16 scale-0 rounded-md bg-foreground px-2.5 py-1 text-xs font-semibold text-background transition-all duration-200 group-hover:scale-100 z-50 whitespace-nowrap shadow-md">
                                    {user.displayName || 'Account'}
                                </span>
                            </button>
                        )}

                        {/* User Account Popover Menu */}
                        {isUserMenuOpen && (
                            <div className="absolute bottom-14 left-0 w-64 z-50 rounded-2xl bg-card/95 backdrop-blur-xl p-3 shadow-2xl border border-border animate-slide-in space-y-2">
                                {/* Profile Info Header */}
                                <div className="flex items-center gap-3 px-2 py-2 border-b border-border/80">
                                    <img
                                        src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}`}
                                        alt={user.displayName || 'User'}
                                        className="h-10 w-10 rounded-full border border-border object-cover shrink-0"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-bold text-foreground truncate">{user.displayName || 'Creator'}</p>
                                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                                    </div>
                                </div>

                                {/* Menu Links */}
                                <div className="space-y-1">
                                    <Link 
                                        to={`/profile/${user.uid}`} 
                                        onClick={() => setIsUserMenuOpen(false)}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-foreground hover:bg-muted transition-colors"
                                    >
                                        <UserIcon className="h-4 w-4 text-muted-foreground" />
                                        <span>View / Edit Profile</span>
                                    </Link>

                                    {/* Appearance Theme Toggle */}
                                    <button
                                        type="button"
                                        onClick={toggleTheme}
                                        className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold text-foreground hover:bg-muted transition-colors cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2.5">
                                            {theme === 'dark' ? <SunIcon className="h-4 w-4 text-amber-400" /> : <MoonIcon className="h-4 w-4 text-slate-700" />}
                                            <span>Appearance</span>
                                        </div>
                                        <span className="text-[10px] text-muted-foreground font-medium px-2 py-0.5 rounded-md bg-muted border border-border">
                                            {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                                        </span>
                                    </button>
                                </div>

                                <div className="border-t border-border/80 pt-1">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsUserMenuOpen(false);
                                            logout();
                                            navigate('/');
                                        }}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-red-500 hover:bg-red-500/10 transition-colors cursor-pointer"
                                    >
                                        <LogoutIcon className="h-4 w-4 text-red-500" />
                                        <span>Log Out</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="w-full">
                        {isExpanded ? (
                            <div className="flex items-center gap-1.5 w-full">
                                <Link to="/auth" className="flex-1 min-w-0">
                                    <Button className="w-full h-10 text-xs font-bold rounded-xl shadow-sm">
                                        Sign In
                                    </Button>
                                </Link>
                                <button
                                    type="button"
                                    onClick={toggleTheme}
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-muted/20 hover:bg-muted/60 hover:border-border text-muted-foreground hover:text-foreground transition-all cursor-pointer shadow-2xs"
                                    title={theme === 'dark' ? "Switch to Light Mode" : "Switch to Dark Mode"}
                                    aria-label="Toggle theme"
                                >
                                    {theme === 'dark' ? (
                                        <SunIcon className="h-4 w-4 text-amber-400" />
                                    ) : (
                                        <MoonIcon className="h-4 w-4 text-slate-700" />
                                    )}
                                </button>
                            </div>
                        ) : (
                            <Link to="/auth" className="group relative flex h-11 w-11 mx-auto items-center justify-center rounded-xl border border-border bg-muted/30 hover:bg-muted">
                                <UserIcon className="h-5 w-5" />
                                <span className="absolute left-16 scale-0 rounded-md bg-foreground px-2.5 py-1 text-xs font-semibold text-background transition-all duration-200 group-hover:scale-100 z-50 whitespace-nowrap shadow-md">
                                    Sign In
                                </span>
                            </Link>
                        )}
                    </div>
                )}
            </div>
        </aside>
    );
};

export default Sidebar;
